<template>
    <div class="flex flex-column gap-3">
        <div class="flex flex-column xl:flex-row lg:flex-row md:flex-row gap-3">
            <div class="flex flex-column w-full gap-2">
                <span class="text-sm">Причина <span class="text-red-500">*</span>:</span>
                <InputText v-model="form.reason" class="w-full" placeholder="Скам" />
            </div>
            <div class="flex flex-column w-full gap-2">
                <span class="text-sm">Источник <span class="text-red-500">*</span>:</span>
                <InputText v-model="form.source" class="w-full" placeholder="admin" />
            </div>
        </div>

        <div class="flex flex-column xl:flex-row lg:flex-row md:flex-row gap-3">
            <div class="flex flex-column w-full gap-2">
                <span class="text-sm">Активность <span class="text-red-500">*</span>:</span>
                <SelectButton v-model="form.active" :options="activeOptions" optionLabel="name" optionValue="value" aria-labelledby="blacklist-active" />
            </div>
            <div class="flex flex-column w-full gap-2">
                <span class="text-sm">Recipient ID:</span>
                <InputText v-model="form.recipient_id" class="w-full" placeholder="Опционально для ручного добавления" />
            </div>
        </div>

        <div v-if="isRecipientIdOnly" class="surface-100 border-round-md p-3 text-xs text-600">
            Для записи по Recipient ID будет отправлен только recipient_id вместе с reason, source и active. Payload получателя в этом режиме не отправляется.
        </div>

        <div v-else class="surface-100 border-round-md p-3">
            <div class="text-sm mb-2">Payload получателя</div>
            <div class="text-xs text-600">Для ручного добавления используется объект recipient. Recipient ID здесь опционален и нужен для сценария запуска из обычного списка.</div>
        </div>

        <div v-if="!isRecipientIdOnly" class="flex flex-column xl:flex-row lg:flex-row md:flex-row gap-3">
            <div class="flex flex-column w-full gap-2">
                <span class="text-sm">Валюта <span class="text-red-500">*</span>:</span>
                <Select
                    v-model="form.payload.currency_code"
                    :options="currencies"
                    optionLabel="code"
                    optionValue="code"
                    :showClear="true"
                    filter
                    class="w-full"
                    placeholder="Выберите валюту"
                >
                    <template #value="slotProps">
                        <span v-if="slotProps.value" class="uppercase">{{ slotProps.value }}</span>
                        <span v-else>{{ slotProps.placeholder }}</span>
                    </template>
                    <template #option="slotProps">
                        <span class="uppercase">{{ slotProps.option.code }}</span>
                    </template>
                </Select>
            </div>

            <div class="flex flex-column w-full gap-2">
                <span class="text-sm">Платежная система <span class="text-red-500">*</span>:</span>
                <Dropdown
                    v-model="form.payload.payment_system_code"
                    :options="availablePaySystems"
                    optionLabel="code"
                    optionValue="code"
                    :showClear="true"
                    filter
                    class="w-full"
                    placeholder="Выберите платежную систему"
                >
                    <template #value="slotProps">
                        <span v-if="slotProps.value">{{ getPaySystemTitleByCode(slotProps.value) }}</span>
                        <span v-else>{{ slotProps.placeholder }}</span>
                    </template>
                    <template #option="slotProps">
                        <span>{{ getPaySystemTitle(slotProps.option) }}</span>
                    </template>
                </Dropdown>
            </div>
        </div>

        <div v-if="!isRecipientIdOnly" class="flex flex-column gap-2">
            <span class="text-sm">Банк:</span>
            <Dropdown
                v-model="form.payload.bank_code"
                :options="availableBanks"
                optionLabel="code"
                optionValue="code"
                :showClear="true"
                filter
                class="w-full"
                placeholder="Выберите банк"
            >
                <template #value="slotProps">
                    <span v-if="slotProps.value">{{ getBankTitleByCode(slotProps.value) }}</span>
                    <span v-else>{{ slotProps.placeholder }}</span>
                </template>
                <template #option="slotProps">
                    <span>{{ getBankTitle(slotProps.option) }}</span>
                </template>
            </Dropdown>
        </div>

        <div v-if="!isRecipientIdOnly && schemaFields.length" class="flex flex-column gap-2">
            <span class="text-sm">Реквизиты:</span>
            <RecipientFieldsEdit
                :key="recipientFieldsKey"
                :fields="schemaFields"
                :requiredFields="schemaRequiredFields"
                :currency="selectedCurrency"
                :initialFields="form.payload.fields"
                :recipientId="form.recipient_id || initialEntryId"
                @check_valid="onRecipientFieldsValid"
                @return_fields="onRecipientFieldsChange"
            />
        </div>

        <div v-else-if="!isRecipientIdOnly" class="surface-50 border-1 surface-border border-round-md p-3 text-xs text-600">
            Выберите валюту и платежную систему. Если для схемы нужен конкретный банк, выберите и его.
        </div>

        <div class="flex justify-content-end gap-2 mt-2">
            <Button
                label="Отмена"
                severity="secondary"
                outlined
                class="border-round-lg border-0"
                @click="$emit('cancel')"
            />
            <Button
                :label="mode === 'edit' ? 'Сохранить' : 'Добавить'"
                severity="success"
                class="border-round-lg border-0"
                :loading="saving"
                :disabled="!canSubmit || saving"
                @click="submitForm"
            />
        </div>
    </div>
</template>

<script>
import Helper from '@/assets/js/Helper'
import RecipientFieldsEdit from '@/components/widgets/RecipientFieldsEdit.vue'
import { BankList, PaymentSystemList } from '@/assets/js/classes/PaymentSystem.js'

const createDefaultForm = () => ({
    recipient_id: '',
    reason: '',
    source: 'admin',
    active: true,
    payload: {
        currency_code: null,
        bank_code: null,
        payment_system_code: null,
        fields: {}
    }
})

export default {
    name: 'RecipientsBlacklistForm',
    components: {
        RecipientFieldsEdit
    },
    props: {
        mode: {
            type: String,
            default: 'add'
        },
        initialEntry: {
            type: Object,
            default: () => ({})
        },
        saving: {
            type: Boolean,
            default: false
        }
    },
    emits: ['cancel', 'submit'],
    data() {
        return {
            currencies: [],
            paySystems: [],
            banks: [],
            form: createDefaultForm(),
            schemaFields: [],
            schemaRequiredFields: [],
            recipientFieldsValid: false,
            activeOptions: [
                { name: 'Да', value: true },
                { name: 'Нет', value: false }
            ]
        }
    },
    computed: {
        availablePaySystems() {
            return (this.paySystems || []).filter((item) => {
                if (!item) return false
                if (item.direction && item.direction == 'get') return false
                if (!this.form.payload.currency_code) return true
                return item.currency_code === this.form.payload.currency_code || item.currency_code === null
            })
        },
        availableBanks() {
            return (this.banks || []).filter((item) => {
                if (!item) return false
                if (this.form.payload.payment_system_code && item.type !== this.form.payload.payment_system_code) return false
                if (this.form.payload.currency_code && item.currency_code !== this.form.payload.currency_code) return false
                return true
            })
        },
        selectedCurrency() {
            if (!this.form.payload.currency_code) return null
            return (this.currencies || []).find((item) => item.code === this.form.payload.currency_code) || { code: this.form.payload.currency_code }
        },
        recipientFieldsKey() {
            return [
                this.mode,
                this.form.payload.currency_code || '-',
                this.form.payload.payment_system_code || '-',
                this.form.payload.bank_code || '-'
            ].join(':')
        },
        initialEntryId() {
            return this.initialEntry && (this.initialEntry.id || this.initialEntry._id) ? (this.initialEntry.id || this.initialEntry._id) : null
        },
        hasPayloadData() {
            const payload = this.form.payload || {}
            if (payload.currency_code) return true
            if (payload.bank_code) return true
            if (payload.payment_system_code) return true
            return Object.keys(payload.fields || {}).length > 0
        },
        isRecipientIdOnly() {
            if (!this.form.recipient_id || !String(this.form.recipient_id).trim()) return false
            return !this.hasPayloadData
        },
        payloadShapeValid() {
            if (!this.form.payload.currency_code) return false
            if (!this.form.payload.payment_system_code) return false
            if (!this.schemaFields.length) return false
            return this.recipientFieldsValid
        },
        canSubmit() {
            if (!this.form.reason || !String(this.form.reason).trim()) return false
            if (!this.form.source || !String(this.form.source).trim()) return false
            if (typeof this.form.active !== 'boolean') return false
            if (this.isRecipientIdOnly) return true
            return this.payloadShapeValid
        }
    },
    watch: {
        initialEntry: {
            immediate: true,
            deep: true,
            async handler() {
                this.form = this.normalizeEntry(this.initialEntry)
                await this.syncSchema()
            }
        },
        'form.payload.currency_code': async function () {
            this.form.payload.payment_system_code = this.isCurrentPaySystemValid() ? this.form.payload.payment_system_code : null
            this.form.payload.bank_code = this.isCurrentBankValid() ? this.form.payload.bank_code : null
            await this.syncSchema()
        },
        'form.payload.payment_system_code': async function () {
            this.form.payload.bank_code = this.isCurrentBankValid() ? this.form.payload.bank_code : null
            await this.syncSchema()
        },
        'form.payload.bank_code': async function () {
            await this.syncSchema()
        }
    },
    methods: {
        isSameFields(left, right) {
            const leftObj = left || {}
            const rightObj = right || {}
            const leftKeys = Object.keys(leftObj)
            const rightKeys = Object.keys(rightObj)
            if (leftKeys.length !== rightKeys.length) return false
            return leftKeys.every((key) => leftObj[key] === rightObj[key])
        },
        normalizeEntry(entry) {
            const form = createDefaultForm()
            const payload = entry && entry.payload ? entry.payload : (entry && entry.recipient ? entry.recipient : null)

            form.recipient_id = entry && entry.recipient_id ? entry.recipient_id : ''
            form.reason = entry && entry.reason ? entry.reason : ''
            form.source = entry && entry.source ? entry.source : 'admin'
            form.active = entry && typeof entry.active === 'boolean' ? entry.active : true

            if (payload) {
                form.payload.currency_code = payload.currency_code || null
                form.payload.bank_code = payload.bank_code || null
                form.payload.payment_system_code = payload.payment_system_code || null
                form.payload.fields = this.compactFields(payload.fields || {})
            }

            return form
        },
        compactFields(fields) {
            const result = {}
            const source = fields || {}
            Object.keys(source).forEach((key) => {
                if (source[key] === null || source[key] === undefined || source[key] === '') return
                result[key] = source[key]
            })
            return result
        },
        isCurrentPaySystemValid() {
            if (!this.form.payload.payment_system_code) return false
            return this.availablePaySystems.some((item) => item.code === this.form.payload.payment_system_code)
        },
        isCurrentBankValid() {
            if (!this.form.payload.bank_code) return false
            return this.availableBanks.some((item) => item.code === this.form.payload.bank_code)
        },
        getPaySystemTitle(item) {
            if (!item) return '-'
            const title = item.titles && item.titles.ru ? item.titles.ru : (item.code || '-')
            return `${title} (${item.code || '-'})`
        },
        getPaySystemTitleByCode(code) {
            const paySystem = (this.paySystems || []).find((item) => item.code === code)
            return this.getPaySystemTitle(paySystem)
        },
        getBankTitle(item) {
            if (!item) return '-'
            const title = item.titles && item.titles.ru ? item.titles.ru : (item.code || '-')
            return `${title} (${item.code || '-'})`
        },
        getBankTitleByCode(code) {
            const bank = (this.availableBanks || []).find((item) => item.code === code)
            return this.getBankTitle(bank || (this.banks || []).find((item) => item.code === code))
        },
        onRecipientFieldsValid(value) {
            this.recipientFieldsValid = !!value
            if (!this.schemaRequiredFields.length) this.recipientFieldsValid = true
        },
        onRecipientFieldsChange(fields) {
            const nextFields = this.compactFields(fields)
            if (!this.isSameFields(this.form.payload.fields, nextFields)) {
                this.form.payload.fields = nextFields
            }
            if (!this.schemaRequiredFields.length) this.recipientFieldsValid = true
        },
        async loadOptions() {
            let currencies = this.$store.getters.GetCurrencies || []
            if (!currencies.length) {
                const res = await this.$store.dispatch('GetCurrencies')
                currencies = res && res.data ? res.data : []
            }
            this.currencies = currencies || []
            this.paySystems = await PaymentSystemList.GetList(this.$store)
            this.banks = await BankList.GetList(this.$store)
        },
        async syncSchema() {
            const currentFields = this.compactFields(this.form.payload.fields)
            const paySystem = (this.paySystems || []).find((item) => item.code === this.form.payload.payment_system_code) || null
            const bank = (this.banks || []).find((item) => item.code === this.form.payload.bank_code && item.type === this.form.payload.payment_system_code) || null

            let schemaFields = []
            let schemaRequiredFields = []

            if (bank && Array.isArray(bank.recipient_fields) && bank.recipient_fields.length) {
                schemaFields = bank.recipient_fields.slice()
                schemaRequiredFields = Array.isArray(bank.recipient_fields_required) ? bank.recipient_fields_required.slice() : []
            } else if (paySystem && Array.isArray(paySystem.recipient_fields) && paySystem.recipient_fields.length) {
                schemaFields = paySystem.recipient_fields.slice()
                schemaRequiredFields = Array.isArray(paySystem.recipient_fields_required) ? paySystem.recipient_fields_required.slice() : []
            } else if (Object.keys(currentFields).length) {
                schemaFields = Object.keys(currentFields)
                schemaRequiredFields = Object.keys(currentFields)
            }

            const filteredFields = {}
            schemaFields.forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(currentFields, key)) filteredFields[key] = currentFields[key]
            })

            this.schemaFields = schemaFields
            this.schemaRequiredFields = schemaRequiredFields
            if (!this.isSameFields(this.form.payload.fields, filteredFields)) {
                this.form.payload.fields = filteredFields
            }
            this.recipientFieldsValid = !schemaRequiredFields.length && !!schemaFields.length
        },
        submitForm() {
            if (!this.canSubmit) return

            if (this.isRecipientIdOnly) {
                const data = {
                    reason: String(this.form.reason).trim(),
                    source: String(this.form.source).trim(),
                    active: this.form.active,
                }

                if (this.mode === 'edit') {
                    this.$emit('submit', {
                        id: this.initialEntryId,
                        data
                    })
                    return
                }

                this.$emit('submit', {
                    recipient_id: String(this.form.recipient_id).trim(),
                    reason: data.reason,
                    source: data.source,
                    active: data.active,
                })
                return
            }

            const payload = {
                currency_code: this.form.payload.currency_code,
                bank_code: this.form.payload.bank_code,
                payment_system_code: this.form.payload.payment_system_code,
                fields: this.compactFields(this.form.payload.fields)
            }

            if (this.mode === 'edit') {
                this.$emit('submit', {
                    id: this.initialEntryId,
                    data: {
                        reason: String(this.form.reason).trim(),
                        source: String(this.form.source).trim(),
                        active: this.form.active,
                        payload
                    }
                })
                return
            }

            this.$emit('submit', {
                recipient_id: this.form.recipient_id ? String(this.form.recipient_id).trim() : null,
                recipient: payload,
                reason: String(this.form.reason).trim(),
                source: String(this.form.source).trim(),
                active: this.form.active,
            })
        }
    },
    async beforeCreate() {
        this.helper = new Helper()
    },
    async mounted() {
        await this.loadOptions()
        await this.syncSchema()
    }
}
</script>